import {
  recordWorkspaceCitationsMutation,
  workspaceCitationReferencesQuery,
  workspaceCitationsQuery,
} from '@affine/graphql';
import { Service } from '@toeverything/infra';
import { BehaviorSubject } from 'rxjs';

import type { DefaultServerService } from '../cloud';
import type { Citation, CitationDocRef } from '../orchestration/types';

/**
 * Workspace-scoped citation store (Phase 7).
 *
 * Durable bibliography backed by the `workspace_citations` Postgres
 * table via the `workspaceCitations` GraphQL query. Phase 2 shipped
 * an in-memory `BehaviorSubject`; that version lost data on reload
 * and couldn't track which doc surfaced each reference. This version:
 *
 *   1. `hydrate(workspaceId)` pulls the full list from the server
 *      when the Citations page mounts (or a workspace activates).
 *   2. Orchestration `citation` events still do optimistic local
 *      upserts so the UI feels instant while the stream is in-flight.
 *   3. On orchestration `complete`, `persist(...)` fires the
 *      `recordWorkspaceCitations` mutation, adding a
 *      `WorkspaceCitationRef` row per (citation, doc) pair.
 *   4. `getReferences(citationId)` fetches the full doc list for the
 *      detail pane (server returns up to 10 via the list query;
 *      this is the "show all" fallback).
 *
 * Optimistic local state and server state live in the same
 * BehaviorSubject — reconciliation merges server rows on top of
 * optimistic ones using the `${type}:${identifier}` dedupe key.
 */
export class WorkspaceCitationStore extends Service {
  private readonly _citations = new BehaviorSubject<Citation[]>([]);
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _lastHydratedWorkspaceId = new BehaviorSubject<
    string | null
  >(null);

  readonly citations$ = this._citations.asObservable();
  readonly loading$ = this._loading.asObservable();

  constructor(private readonly defaultServerService: DefaultServerService) {
    super();
  }

  get citations(): Citation[] {
    return this._citations.getValue();
  }

  private get gql() {
    return this.defaultServerService.server.gql;
  }

  private key(c: { type?: string; identifier?: string }): string {
    return `${c.type ?? ''}:${c.identifier ?? ''}`.toLowerCase();
  }

  /**
   * Fetch the durable list from the server. Idempotent — calling
   * with the same workspaceId while a fetch is in flight returns
   * the existing promise instead of racing. Server rows merge on
   * top of any optimistic local state.
   */
  async hydrate(workspaceId: string): Promise<void> {
    if (this._lastHydratedWorkspaceId.getValue() === workspaceId) return;
    this._loading.next(true);
    try {
      const res = (await this.gql({
        query: workspaceCitationsQuery as any,
        variables: {
          workspaceId,
          filter: {
            limit: 500,
            offset: 0,
            includeDocs: true,
          },
        },
      } as any)) as any;
      const list = res?.currentUser?.orchestration?.citations;
      if (!list) return;
      const items: Citation[] = (list.items ?? []).map((r: any) =>
        gqlRowToCitation(r)
      );
      // Merge server rows on top of any optimistic state collected
      // before hydration finished.
      const merged = mergeByKey(this._citations.getValue(), items, this.key);
      this._citations.next(merged);
      this._lastHydratedWorkspaceId.next(workspaceId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[workspace-citation-store] hydrate failed', err);
    } finally {
      this._loading.next(false);
    }
  }

  /**
   * Optimistic local upsert. Called by orchestration `citation`
   * events during streaming so the Citations page reflects new
   * references without waiting for the `complete`/mutation roundtrip.
   */
  upsertMany(incoming: Citation[]): void {
    if (!incoming?.length) return;
    const merged = mergeByKey(
      this._citations.getValue(),
      incoming,
      this.key
    );
    if (merged !== this._citations.getValue()) {
      this._citations.next(merged);
    }
  }

  /**
   * Persist the given citations against a doc. Called on
   * orchestration `complete`. Returns the number of rows upserted
   * server-side so callers can report.
   */
  async persist(input: {
    workspaceId: string;
    docId: string;
    sessionId?: string;
    citations: Citation[];
    tokenLocations?: Array<{
      type: string;
      identifier: string;
      blockId: string;
    }>;
  }): Promise<number> {
    const { workspaceId, docId, sessionId, citations, tokenLocations } = input;
    if (!citations?.length) return 0;
    const locByKey = new Map<string, string>();
    for (const loc of tokenLocations ?? []) {
      locByKey.set(`${loc.type.toLowerCase()}:${loc.identifier}`, loc.blockId);
    }
    const payload = citations
      .filter(c => c.type && c.identifier)
      .map(c => ({
        type: c.type,
        identifier: c.identifier,
        title: c.title,
        authors: c.authors,
        journal: c.journal,
        year: c.year,
        url: c.url,
        abstract: c.abstract,
        verified: c.verified,
        blockId: locByKey.get(this.key(c)),
      }));
    if (!payload.length) return 0;
    try {
      const res = (await this.gql({
        query: recordWorkspaceCitationsMutation as any,
        variables: {
          input: { workspaceId, docId, sessionId, citations: payload },
        },
      } as any)) as any;
      return (res?.recordWorkspaceCitations as number) ?? 0;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[workspace-citation-store] persist failed', err);
      return 0;
    }
  }

  /**
   * Fetch the full list of doc references for a single citation —
   * used by the Citations page detail pane when the summary shows
   * "10 of 15 docs".
   */
  async getReferences(
    workspaceId: string,
    citationId: string
  ): Promise<CitationDocRef[]> {
    try {
      const res = (await this.gql({
        query: workspaceCitationReferencesQuery as any,
        variables: { workspaceId, citationId },
      } as any)) as any;
      return (
        res?.currentUser?.orchestration?.citationReferences ?? []
      ) as CitationDocRef[];
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[workspace-citation-store] getReferences failed', err);
      return [];
    }
  }

  getById(id: string): Citation | undefined {
    return this._citations
      .getValue()
      .find(c => c.id === id || this.key(c) === id.toLowerCase());
  }

  clear(): void {
    this._citations.next([]);
    this._lastHydratedWorkspaceId.next(null);
  }
}

function mergeByKey(
  current: Citation[],
  incoming: Citation[],
  key: (c: Citation) => string
): Citation[] {
  const byKey = new Map<string, Citation>();
  for (const c of current) byKey.set(key(c), c);
  let changed = false;
  for (const next of incoming) {
    const k = key(next);
    const prev = byKey.get(k);
    if (!prev) {
      byKey.set(k, next);
      changed = true;
    } else {
      const merged: Citation = { ...prev, ...next };
      // Never drop existing provenance data if the new row omits it.
      if (!next.referencedInDocs && prev.referencedInDocs) {
        merged.referencedInDocs = prev.referencedInDocs;
      }
      if (next.referenceCount === undefined && prev.referenceCount !== undefined) {
        merged.referenceCount = prev.referenceCount;
      }
      byKey.set(k, merged);
      changed = true;
    }
  }
  return changed ? Array.from(byKey.values()) : current;
}

function gqlRowToCitation(row: any): Citation {
  return {
    id: row.id,
    type: row.type,
    identifier: row.identifier,
    title: row.title ?? undefined,
    authors: row.authors ?? [],
    journal: row.journal ?? undefined,
    year: row.year ?? undefined,
    url: row.url ?? undefined,
    abstract: row.abstract ?? undefined,
    verified: !!row.verified,
    text: `[${labelType(row.type)}:${row.identifier}]`,
    referenceCount: row.referenceCount ?? 0,
    referencedInDocs: row.referencedInDocs ?? [],
    firstSeenAt: row.firstSeenAt,
    lastSeenAt: row.lastSeenAt,
  };
}

function labelType(t: string): string {
  switch (t) {
    case 'pubmed':
    case 'pmid':
      return 'PMID';
    case 'doi':
      return 'DOI';
    case 'arxiv':
      return 'arXiv';
    default:
      return t;
  }
}
